import { useRef, useEffect } from 'react';
import { Box, Fade, Typography, IconButton } from '@mui/material';
import { CloseRounded } from '@mui/icons-material';

import { DrawerHeaderProps, DrawerHeaderAction } from './DrawerHeader.interface';
import useCompressAnimation, { ANIMATION_DURATION, ANIMATION_EASING, TRANSITION_DELAY } from './useCompressAnimation';

import { useIMCContext } from 'modules/IMC/common/context';

function DrawerHeader({
    title,
    startAction,
    endActions,
    compressed,
    containerRef,
    fixedContent = null,
    children,
}: DrawerHeaderProps) {
    const collapsibleSectionRef = useRef<HTMLDivElement>(null);
    const startAnimating = useRef(false);
    const offsetRef = useRef<HTMLDivElement>(null);

    const { openDrawer } = useIMCContext();
    const { isCompressed } = useCompressAnimation(collapsibleSectionRef, containerRef, offsetRef, compressed);

    const compressedHeader = compressed || isCompressed;

    const StartActionComponent: DrawerHeaderAction | null = startAction || null;

    useEffect(() => {
        // This is to prevent triggering animation delays on first render
        if (!startAnimating.current && isCompressed) {
            startAnimating.current = true;
        }
    }, [isCompressed]);

    return (
        <>
            <Box
                sx={{
                    position: 'sticky',
                    top: 0,
                    backgroundColor: 'cement.200',
                    paddingX: 3,
                    paddingTop: 2.5,
                    paddingBottom: fixedContent && !compressed ? 0 : 2,
                    zIndex: 1,
                }}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                        gridTemplateAreas: '"start center end"',
                        width: '100%',
                        textAlign: 'center',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                    }}>
                    {StartActionComponent && (
                        <Box
                            sx={{
                                gridArea: 'start',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                            }}>
                            <StartActionComponent sx={{ padding: 0 }} />
                        </Box>
                    )}
                    <Fade
                        in={compressedHeader}
                        style={{ transitionDelay: `${TRANSITION_DELAY}ms` }}
                        timeout={ANIMATION_DURATION}
                        easing={ANIMATION_EASING}>
                        <Typography variant="overline" sx={{ gridArea: 'center', lineHeight: '24px' }}>
                            {title}
                        </Typography>
                    </Fade>
                    <Box
                        sx={{
                            gridArea: 'end',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 2,
                        }}>
                        <IconButton aria-label="Close" onClick={() => openDrawer(null)} sx={{ padding: 0 }}>
                            <CloseRounded />
                        </IconButton>
                        {endActions?.map((action) => {
                            const ActionComponent: DrawerHeaderAction = action;

                            return <ActionComponent key={`action-${ActionComponent.name}`} sx={{ padding: 0 }} />;
                        })}
                    </Box>
                </Box>
                <Box
                    ref={collapsibleSectionRef}
                    sx={{
                        paddingBottom: '5px',
                        transition: `height ${ANIMATION_DURATION}ms ${ANIMATION_EASING}, padding-bottom ${ANIMATION_DURATION}ms ${ANIMATION_EASING}, overflow ${ANIMATION_DURATION}ms ${ANIMATION_EASING}`,
                    }}>
                    <div>
                        <Fade
                            in={!compressedHeader}
                            appear={false}
                            timeout={ANIMATION_DURATION}
                            easing={ANIMATION_EASING}
                            style={{
                                transitionDelay:
                                    !compressedHeader && startAnimating.current ? `${ANIMATION_DURATION}ms` : '0ms',
                            }}>
                            <Typography
                                variant="h3"
                                sx={{
                                    textAlign: 'center',
                                    paddingBottom: 3,
                                    transform: compressedHeader ? 'translateY(-4px)' : 'translateY(0)',
                                    '&': {
                                        transitionProperty: 'all!important',
                                    },
                                }}>
                                {title}
                            </Typography>
                        </Fade>

                        <Fade
                            in={!compressedHeader}
                            appear={false}
                            style={{
                                transitionDelay: startAnimating.current ? `${ANIMATION_DURATION + 100}ms` : '0ms',
                            }}
                            easing={ANIMATION_EASING}>
                            <Box>{children}</Box>
                        </Fade>
                    </div>
                </Box>
                {fixedContent && !compressed && fixedContent}
            </Box>
            <Box ref={offsetRef} />
        </>
    );
}

export default DrawerHeader;
